import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { isEmpty } from 'lodash'
import { useTracking, track } from 'lib/analytics'
import { ProductFiltersShape, SortParamsShape } from 'store/lib/prop-types'

import { getHistory } from 'lib/history'
import {
  getSearchId,
  PRODUCT_SEARCH_INDEX_KEY,
} from 'shared/hooks/useSearchId/useSearchId'
import { mapHeroesToProductCards } from '../product-grid'
import { withActiveSortParams, withActiveProductFilters } from '../../reducers'
import { useBrandsSearch, useProductsSearch } from '../../api/hooks'
import { BaseSearchPage } from './search-page'
import { getSearchHistory } from '../store-search/helpers'

export const SearchPageWrapper = ({ location, productFilters, sortParams }) => {
  const searchTerm = location?.query?.search_term || ''
  const { data: productData, isLoading: productIsLoading } = useProductsSearch(
    searchTerm,
    {
      heroes: true,
      filters: productFilters,
      orderBy: {
        key: sortParams?.sort,
        direction: sortParams?.order,
      },
    }
  )

  const { data: brandData, isLoading: brandIsLoading } =
    useBrandsSearch(searchTerm)

  const tracker = useTracking()
  const searchId = getSearchId(PRODUCT_SEARCH_INDEX_KEY)
  const lastSearchTerm = getSearchHistory()[0]

  const trackSearch = () => {
    const executedSearch = lastSearchTerm === searchTerm || false
    tracker.trackEvent({
      event: track.searchResultsPageViewed,
      eventType: track.EventType.SHOP,
      eventLocation: track.EventLocation.SHOP,
      searchId,
      searchTerm,
      resultCount: productData?.heroProducts?.length || 0,
      top20Results: productData?.heroProducts
        ?.slice(0, 20)
        .map((product) => product.id.toString()),
      executedSearch,
    })

    // TODO: Remove this duplicate event once all platforms are using the Search Results Page Viewed event
    tracker.trackEvent({
      event: track.storeSearchViewed,
      storeSearchTerms: searchTerm,
    })
  }

  const updateUrlSortParams = () => {
    const history = getHistory({ useRouter: true })
    if (!location.query.sort && !location.query.order && !isEmpty(sortParams)) {
      const nextQuery = {
        ...location.query,
        ...sortParams,
      }
      history.replace({
        pathname: location.pathname,
        query: nextQuery,
        state: location.state,
      })
    }
  }

  useEffect(() => {
    updateUrlSortParams()
    if (!productIsLoading) {
      trackSearch()
    }
  }, [searchTerm, productIsLoading])

  const productCards = productIsLoading
    ? null
    : productData.heroProducts.map(mapHeroesToProductCards)
  const heroProducts = productIsLoading ? null : productData.heroProducts
  const brand = brandIsLoading ? null : brandData

  return (
    <BaseSearchPage
      brand={brand}
      heroProducts={heroProducts}
      productCards={productCards}
      searchTerm={searchTerm}
    />
  )
}

SearchPageWrapper.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    query: PropTypes.shape({
      search_term: PropTypes.string,
      sort: PropTypes.string,
      order: PropTypes.string,
    }),
    state: PropTypes.string,
  }).isRequired,
  productFilters: PropTypes.shape(ProductFiltersShape).isRequired,
  sortParams: PropTypes.shape(SortParamsShape).isRequired,
}

const SearchPage = compose(
  withActiveProductFilters(),
  withActiveSortParams()
)(SearchPageWrapper)

export default SearchPage
